import React from 'react'
import './App.scss'

import { BrowserRouter as Router, Route, Switch } from 'react-router-dom'
import { ProductDataContext } from './ProductDataContext'
// eslint-disable-next-line no-restricted-imports
import { LocalProductDataContextImplementation } from './LocalProductDataContextImplementation'
import { RouteComponentProps } from 'react-router'
import { ProductSeries } from './types/productTypes'
import { SharedListLoaderScreen } from './list/SharedListScreen'
import FirebaseApp from './FirebaseApp'
import ConfiguratorApp from './ConfiguratorApp'
import LoadingScreen from './components/LoadingScreen'
import { isDev, PUBLIC_URL } from './environment'
import { shouldUseFakeAuth, shouldUseLocalProducts } from './debug/debugModeUtils'
import { useProducts } from './hooks/useProducts'
import DemoLinks from './debug/DemoLinks'
import { EnvironmentHeader } from './debug/EnvironmentHeader'
import ItemNoQueryParam from './ItemNoQueryParam'
import { CompositeContextProvider, ContextConfig } from './components/CompositeContextProvider'
import { ProductPricingContext } from './productPricing/ProductPricingContext'
import { useRetailProductPriceStore } from './hooks/useProductCataloguePrices'
import { ProductDataShape } from './ProductDataShape'
import { LegacyListUrlRedirecter } from './LegacyListUrlRedirecter'
import { useBuilderMode } from './useBuilderMode'
import { getBuilderModeConfiguration } from './getBuilderModeConfiguration'

function App() {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const [loadingProductData, productDataError, productData] = useProducts(FirebaseApp, shouldUseLocalProducts())

  React.useEffect(() => {
    if (isDev()) {
      document.title = `DEV ${process.env.REACT_APP_NAME ?? ''}`
    }
  }, [])

  const builderMode = useBuilderMode()

  const retailPricingContextValue = useRetailProductPriceStore()

  const productDataContextValue: ProductDataShape = productDataError ? LocalProductDataContextImplementation : productData
  const productDataContextConfig: ContextConfig<ProductDataShape> = { context: ProductDataContext, value: productDataContextValue }

  return (
    <div className="App">
      <CompositeContextProvider
        contexts={[
          productDataContextConfig,
        ]}
      >
        {/* paths hardcoded for legacy redirect */}
        <LegacyListUrlRedirecter
          toBasePathMatch="/clipsal-switch-socket-builder"
          fromBasePath="/build-iconic/"
        />

        <EnvironmentHeader />

        <Router basename={PUBLIC_URL}>
          <Switch>
            <Route
              path="/list/:listId"
              render={
                (routeProps) => {
                  const params = new URLSearchParams(routeProps.location.search)

                  return (
                    <ProductPricingContext.Provider value={retailPricingContextValue}>
                      <SharedListLoaderScreen
                        listId={routeProps.match.params.listId}
                        viewType={params.get('view')}
                      />
                    </ProductPricingContext.Provider>
                  )
                }
              }
            />

            {
              process.env.REACT_APP_ENABLE_DEMO === 'true' && (
                <Route path="/demo">
                  <DemoLinks/>
                </Route>
              )
            }

            <Route
              path="/"
              render={(routeProps: RouteComponentProps) => {
                const params = new URLSearchParams(routeProps.location.search)

                // Determine product series from query parameter
                const seriesParam = params.get('series')
                const productSeries = seriesParam === 'pro' ? ProductSeries.Pro : ProductSeries.Iconic

                if (loadingProductData) {
                  return (
                    <LoadingScreen />
                  )
                }

                const isAuthenticated = shouldUseFakeAuth()
                const openProductItemNo = params.get(ItemNoQueryParam)

                const {
                  pricingContext,
                  defaultAppState,
                  clearListRequested,
                  showPackQuantityInCustomise,
                  lockToElectricianUserType,
                  proSeriesEnabled,
                  addingRoomsEnabled,
                  canPersistUserData,
                } = getBuilderModeConfiguration(builderMode, retailPricingContextValue)

                return (
                  <ProductPricingContext.Provider value={pricingContext}>
                    <ConfiguratorApp
                      deepLinkItemNo={openProductItemNo}
                      isAuthenticated={isAuthenticated}
                      shouldShowPackQuantityInCustomise={showPackQuantityInCustomise}
                      clearList={clearListRequested}
                      lockToElectricianUserType={lockToElectricianUserType}
                      proSeriesEnabled={proSeriesEnabled}
                      addingRoomsEnabled={addingRoomsEnabled}
                      canPersistUserData={canPersistUserData}
                      defaultAppState={defaultAppState}
                      productSeries={productSeries}
                      routeHistory={routeProps.history}
                    />
                  </ProductPricingContext.Provider>
                )
              }}
            />

            {/*{ productDataError && (*/}
            {/*  <h3>Something went wrong</h3>*/}
            {/*)}*/}

          </Switch>
        </Router>
      </CompositeContextProvider>
      { isDev() && productDataError && (
        <div
          style={{
            background: '#f00',
            opacity: 0.3,
            color: '#fff',
            position: 'fixed',
            top: '4px',
            left: '4px',
            zIndex: 100,
            padding: '2px 6px',
          }}
        >
          Couldn&apos;t load from cloud
        </div>
      )}
    </div>
  )
}

export default App
